import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import { LEVEL_COLORS, CATEGORY_EMOJIS } from '../utils/api';

const getImages = (product) => {
  if (product.images) {
    try { return typeof product.images === 'string' ? JSON.parse(product.images) : product.images; } catch {}
  }
  return product.image ? [product.image] : [];
};

// ─── IMAGE LIGHTBOX ──────────────────────────────────────────────────────────
function Lightbox({ images, startIndex, onClose }) {
  const [current, setCurrent] = useState(startIndex);
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column'
    }}>
      <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '80vh' }}>
        <img src={images[current]} alt="" style={{ maxWidth: '90vw', maxHeight: '75vh', objectFit: 'contain', borderRadius: 8 }} />
        {images.length > 1 && (
          <>
            <button onClick={() => setCurrent(i => (i - 1 + images.length) % images.length)} style={arrowBtn('right')}>❮</button>
            <button onClick={() => setCurrent(i => (i + 1) % images.length)} style={arrowBtn('left')}>❯</button>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          {images.map((img, i) => (
            <img key={i} src={img} alt="" onClick={e => { e.stopPropagation(); setCurrent(i); }}
              style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, cursor: 'pointer',
                border: i === current ? '3px solid white' : '3px solid transparent', opacity: i === current ? 1 : 0.6 }} />
          ))}
        </div>
      )}
      <button onClick={onClose} style={{ position: 'fixed', top: 20, left: 20, background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', fontSize: 28, borderRadius: '50%', width: 44, height: 44, cursor: 'pointer' }}>✕</button>
    </div>
  );
}

const arrowBtn = (side) => ({
  position: 'absolute', top: '50%', [side]: -50, transform: 'translateY(-50%)',
  background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
  fontSize: 24, borderRadius: '50%', width: 40, height: 40, cursor: 'pointer'
});

// ─── RECOMMENDATION POPUP ───────────────────────────────────────────────────
// Shown right after a product with a recommended_product_id is added to the
// cart. Lets the customer add the recommended item with one click, or skip.
function RecommendationPopup({ product, onClose }) {
  const { addItem } = useCart();
  const recPrice = product.recommended_product_sale_price || product.recommended_product_price;
  // treat null/undefined stock as "in stock" — admin can set it to 0 explicitly to block
  const outOfStock = product.recommended_product_stock !== null
    && product.recommended_product_stock !== undefined
    && product.recommended_product_stock <= 0;

  const recommendedProduct = {
    id: product.recommended_product_id,
    name: product.recommended_product_name,
    image: product.recommended_product_image,
    price: product.recommended_product_price,
    sale_price: product.recommended_product_sale_price,
    stock_quantity: product.recommended_product_stock ?? 999,
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: 16, width: '100%', maxWidth: 380,
        padding: 24, direction: 'rtl', textAlign: 'center'
      }}>
        <div style={{ fontSize: 14, color: 'var(--text-light)', marginBottom: 4 }}>💡 מומלץ להוסיף</div>
        <div style={{
          width: 90, height: 90, margin: '8px auto 12px', borderRadius: 10,
          background: 'var(--bg-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', fontSize: 40
        }}>
          {recommendedProduct.image ? <img src={recommendedProduct.image} alt={recommendedProduct.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : '🎨'}
        </div>
        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--brown-dark)', marginBottom: 4 }}>{recommendedProduct.name}</div>
        <div style={{ marginBottom: 18 }}>
          <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--primary-dark)' }}>₪{recPrice.toFixed(2)}</span>
          {recommendedProduct.sale_price && (
            <span style={{ fontSize: 14, color: 'var(--text-light)', textDecoration: 'line-through', marginRight: 8 }}>₪{recommendedProduct.price.toFixed(2)}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button className="btn btn-cancel" onClick={onClose}>לא תודה</button>
          <button className="btn btn-primary" 
            disabled={outOfStock}
            style={outOfStock ? { background: '#bbb', color: 'white', cursor: 'not-allowed' } : {}}
            onClick={() => { addItem(recommendedProduct); onClose(); }}>
            {outOfStock ? 'אזל מהמלאי' : (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                </svg>
                הוסף להזמנה
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PRODUCT DETAIL MODAL ────────────────────────────────────────────────────
function ProductDetail({ product, onClose }) {
  const { addItem, items } = useCart();
  const inCart = items.find(i => i.id === product.id);
  const images = getImages(product);
  const [lightbox, setLightbox] = useState(null);
  const [mainImg, setMainImg] = useState(0);
  const [showRecommendation, setShowRecommendation] = useState(false);

  const handleAddToCart = () => {
    addItem(product);
    if (product.recommended_product_id) {
      setShowRecommendation(true);
    } else {
      onClose();
    }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: 16, width: '100%', maxWidth: 680,
        maxHeight: '90vh', overflow: 'auto', direction: 'rtl'
      }}>
        {/* תמונה ראשית */}
        <div style={{ position: 'relative', background: '#f8f8f8', height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '16px 16px 0 0', overflow: 'hidden' }}>
          {images.length > 0 ? (
            <img src={images[mainImg]} alt={product.name}
              onClick={() => setLightbox(mainImg)}
              style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: 'zoom-in', padding: 8 }} />
          ) : (
            <span style={{ fontSize: 80 }}>{CATEGORY_EMOJIS[product.category_name] || '🎨'}</span>
          )}
          {images.length > 0 && (
            <button onClick={() => setLightbox(mainImg)} style={{
              position: 'absolute', bottom: 12, left: 12,
              background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none',
              borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13
            }}>🔍 הגדל</button>
          )}
          <button onClick={onClose} style={{
            position: 'absolute', top: 12, left: 12,
            background: 'rgba(0,0,0,0.4)', color: 'white', border: 'none',
            borderRadius: '50%', width: 36, height: 36, fontSize: 18, cursor: 'pointer'
          }}>✕</button>
          <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {product.sale_price && <span className="badge badge-sale">מבצע!</span>}
            {(product.stock_quantity <= 0) ? <span className="badge badge-oos">אזל</span> : null}
          </div>
        </div>

        {/* תמונות נוספות */}
        {images.length > 1 && (
          <div style={{ display: 'flex', gap: 8, padding: '10px 20px', background: 'var(--bg-soft)' }}>
            {images.map((img, i) => (
              <img key={i} src={img} alt="" onClick={() => setMainImg(i)}
                style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, cursor: 'pointer',
                  border: i === mainImg ? '3px solid var(--primary-dark)' : '3px solid transparent' }} />
            ))}
          </div>
        )}

        {/* פרטים */}
        <div style={{ padding: '20px 24px' }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--brown-dark)', marginBottom: 8 }}>{product.name}</h2>
          <p style={{ color: 'var(--text-light)', marginBottom: 16, lineHeight: 1.7 }}>{product.description}</p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
            <span className="meta-chip" style={{ color: LEVEL_COLORS[product.level], fontWeight: 700 }}>● רמה: {product.level}</span>
            <span className="meta-chip">👶 גיל מינימום: {product.min_age}+</span>
            {(product.extra_categories && product.extra_categories.length > 0 ? product.extra_categories : product.category_name ? [{name: product.category_name}] : []).map(c => <span key={c.name} className="meta-chip">📂 {c.name}</span>)}
            {product.stock_quantity > 0 && product.stock_quantity <= 5 && (
              <span className="meta-chip" style={{ color: '#E67E22', fontWeight: 700 }}>⚠️ נותרו {product.stock_quantity} יחידות</span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '2px solid var(--bg-soft)', paddingTop: 16 }}>
            <div>
              <span style={{ fontSize: 28, fontWeight: 800, color: 'var(--text)' }}>
                ₪{(product.sale_price || product.price).toFixed(2)}
              </span>
              {product.sale_price && (
                <span style={{ fontSize: 16, color: 'var(--text-light)', textDecoration: 'line-through', marginRight: 10 }}>
                  ₪{product.price.toFixed(2)}
                </span>
              )}
            </div>
            <button className="add-to-cart-btn" style={{ padding: '12px 24px', fontSize: 16 }}
              disabled={!!(product.stock_quantity <= 0)}
              onClick={handleAddToCart}>
              {(product.stock_quantity <= 0) ? 'אזל מהמלאי' : inCart ? '✓ בעגלה' : (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                  </svg>
                  הוסף לעגלה
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
      {lightbox !== null && <Lightbox images={images} startIndex={lightbox} onClose={() => setLightbox(null)} />}
      {showRecommendation && (
        <RecommendationPopup product={product} onClose={() => { setShowRecommendation(false); onClose(); }} />
      )}
    </div>
  );
}

// ─── FLY TO CART ANIMATION ─────────────────────────────────────────────────
function flyToCart(fromEl) {
  const cartBtn = document.querySelector('.cart-btn');
  if (!cartBtn || !fromEl) return;
  const fromRect = fromEl.getBoundingClientRect();
  const toRect = cartBtn.getBoundingClientRect();
  const fly = document.createElement('div');
  fly.className = 'fly-to-cart';
  fly.textContent = '🎨';
  fly.style.left = (fromRect.left + fromRect.width / 2 - 20) + 'px';
  fly.style.top  = (fromRect.top  + fromRect.height / 2 - 20) + 'px';
  document.body.appendChild(fly);
  requestAnimationFrame(() => {
    fly.style.transition = 'all 0.55s cubic-bezier(.4,0,.2,1)';
    fly.style.left = (toRect.left + toRect.width / 2 - 20) + 'px';
    fly.style.top  = (toRect.top  + toRect.height / 2 - 20) + 'px';
    fly.style.transform = 'scale(0.3)';
    fly.style.opacity = '0.5';
  });
  setTimeout(() => {
    fly.remove();
    cartBtn.classList.add('pulsing');
    setTimeout(() => cartBtn.classList.remove('pulsing'), 600);
    const badge = cartBtn.querySelector('.cart-badge');
    if (badge) { badge.classList.add('bouncing'); setTimeout(() => badge.classList.remove('bouncing'), 500); }
  }, 560);
}

// ─── PRODUCT CARD ─────────────────────────────────────────────────────────────
export default function ProductCard({ product }) {
  const { addItem, items } = useCart();
  const inCart = items.find(i => i.id === product.id);
  const images = getImages(product);
  const [showDetail, setShowDetail] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [showRecommendation, setShowRecommendation] = useState(false);
  const [btnPopping, setBtnPopping] = useState(false);
  const btnRef = React.useRef(null);

  const handleQuickAdd = () => {
    if (product.stock_quantity <= 0) return;
    addItem(product);
    flyToCart(btnRef.current);
    setBtnPopping(true);
    setTimeout(() => setBtnPopping(false), 400);
    if (product.recommended_product_id) setShowRecommendation(true);
  };

  return (
    <>
      <div className={`product-card ${(product.stock_quantity <= 0) ? 'out-of-stock' : ''}`}>
        {/* תמונה גדולה */}
        <div className="product-img-wrap" style={{ height: 240, cursor: 'pointer' }} onClick={() => setShowDetail(true)}>
          {product.image
            ? <img src={product.image} alt={product.name} />
            : <span>{CATEGORY_EMOJIS[product.category_name] || '🎨'}</span>
          }
          {product.image && (
            <button onClick={e => { e.stopPropagation(); setLightbox(true); }} style={{
              position: 'absolute', bottom: 8, left: 8,
              background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none',
              borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', zIndex: 2
            }}>🔍 הגדל</button>
          )}
        </div>

        <div className="product-badges">
          {product.sale_price && <span className="badge badge-sale">מבצע!</span>}
          {(product.stock_quantity <= 0) ? <span className="badge badge-oos">אזל</span> : null}
        </div>

        <div className="product-body">
          <div className="product-name" style={{ cursor: 'pointer' }} onClick={() => setShowDetail(true)}>{product.name}</div>
          <div className="product-desc">{product.description}</div>
          <div className="product-meta">
            <span className="meta-chip" style={{ color: LEVEL_COLORS[product.level] }}>● {product.level}</span>
            <span className="meta-chip">גיל {product.min_age}+</span>
            {product.category_name && <span className="meta-chip">{product.category_name}</span>}
          </div>
          <div className="product-price-row">
            <div>
              <span className="price-new">₪{(product.sale_price || product.price).toFixed(2)}</span>
              {product.sale_price && <span className="price-old">₪{product.price.toFixed(2)}</span>}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="add-to-cart-btn" style={{ background: 'var(--bg-soft)', color: 'var(--primary-dark)', fontSize: 13, padding: '7px 10px' }}
                onClick={() => setShowDetail(true)}>פרטים</button>
              <button ref={btnRef} className={`add-to-cart-btn${btnPopping ? ' popping' : ''}`}
                disabled={!!(product.stock_quantity <= 0)}
                onClick={handleQuickAdd}>
                {(product.stock_quantity <= 0) ? 'אזל' : inCart ? '✓' : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                    </svg>
                    +
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showDetail && <ProductDetail product={product} onClose={() => setShowDetail(false)} />}
      {lightbox && images.length > 0 && <Lightbox images={images} startIndex={0} onClose={() => setLightbox(false)} />}
      {showRecommendation && (
        <RecommendationPopup product={product} onClose={() => setShowRecommendation(false)} />
      )}
    </>
  );
}
